import { createElement, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type MenuAnchor = {
  left: number;
  top: number;
  width?: number;
  /** `top` is the pointer Y; menu grows upward (footer / context clicks). */
  placement?: 'default' | 'above-pointer';
};

type MenuProps = {
  open: boolean;
  anchor: MenuAnchor | null;
  onClose: () => void;
  children: ReactNode;
  minWidth?: number;
  dataAttribute?: string;
  /** Clicks on matching elements do not close the menu (e.g. column headers that open another menu). */
  outsideIgnoreSelector?: string;
};

function clampMenuPosition(
  left: number,
  top: number,
  minWidth: number,
  heightEstimate: number,
): { left: number; top: number } {
  if (typeof window === 'undefined') {
    return { left, top };
  }
  const edge = 8;
  const narrow = window.innerWidth < 640;
  const panelWidth = narrow
    ? Math.min(minWidth, window.innerWidth - edge * 2)
    : minWidth;
  const maxX = window.innerWidth - panelWidth - edge;
  const maxY = window.innerHeight - heightEstimate - edge;
  return {
    left: narrow ? edge : Math.max(edge, Math.min(left, maxX)),
    top: Math.max(edge, Math.min(top, maxY)),
  };
}

export function Menu({
  open,
  anchor,
  onClose,
  children,
  minWidth = 180,
  dataAttribute = 'cs-menu',
  outsideIgnoreSelector,
}: MenuProps) {
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!open || !anchor) {
      return;
    }
    const narrow = typeof window !== 'undefined' && window.innerWidth < 640;
    const panelMinWidth = narrow
      ? Math.min(minWidth, window.innerWidth - 16)
      : minWidth;
    setPosition(
      clampMenuPosition(anchor.left, anchor.top, panelMinWidth, 220),
    );
  }, [open, anchor, minWidth]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onOutsideClick(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.closest(`[data-${dataAttribute}="true"]`)) {
        return;
      }
      if (outsideIgnoreSelector && target?.closest(outsideIgnoreSelector)) {
        return;
      }
      onClose();
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    // Use click (not mousedown) so native <select> option picks inside the menu
    // fire their onChange before the menu unmounts.
    document.addEventListener('click', onOutsideClick, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onOutsideClick, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, dataAttribute, outsideIgnoreSelector]);

  if (!open || !anchor || typeof document === 'undefined') {
    return null;
  }

  const abovePointer = anchor.placement === 'above-pointer';
  const narrowViewport = typeof window !== 'undefined' && window.innerWidth < 640;
  const panelMinWidth =
    narrowViewport && typeof window !== 'undefined'
      ? Math.min(minWidth, window.innerWidth - 16)
      : minWidth;
  const maxHeight =
    typeof window !== 'undefined'
      ? abovePointer
        ? Math.max(120, position.top - 8)
        : Math.max(120, window.innerHeight - position.top - 8)
      : undefined;

  return createPortal(
    createElement(
      'div',
      {
        [`data-${dataAttribute}`]: 'true',
        className: 'cs-menu-panel fixed',
        style: {
          left: position.left,
          top: position.top,
          minWidth: panelMinWidth,
          maxWidth: narrowViewport ? 'calc(100vw - 1rem)' : undefined,
          maxHeight,
          zIndex: 99999,
          transform: abovePointer ? 'translateY(calc(-100% - 4px))' : undefined,
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onContextMenu: (e: MouseEvent) => e.preventDefault(),
      },
      children,
    ),
    document.body,
  );
}

export function MenuItem({
  label,
  icon,
  onPress,
  destructive,
  disabled,
}: {
  label: ReactNode;
  icon?: ReactNode;
  onPress: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  const content =
    icon != null
      ? createElement(
          'span',
          { className: 'cs-menu-item-content' },
          createElement('span', { className: 'cs-menu-item-icon' }, icon),
          createElement('span', { className: 'cs-menu-item-label' }, label),
        )
      : label;

  return createElement(
    'button',
    {
      type: 'button',
      disabled,
      className: disabled
        ? 'cs-menu-item-disabled'
        : destructive
          ? 'cs-menu-item-destructive'
          : 'cs-menu-item',
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
          onPress();
        }
      },
    },
    content,
  );
}

export function MenuDivider() {
  return createElement('div', { className: 'my-1 border-t border-border' });
}

export function anchorBelowElement(el: HTMLElement): MenuAnchor {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.bottom + 4,
    width: rect.width,
  };
}

/** Position a menu above an element (e.g. table footer calculate triggers). */
export function anchorAboveElement(
  el: HTMLElement,
  estimatedHeight = 220,
): MenuAnchor {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left,
    top: Math.max(8, rect.top - estimatedHeight - 4),
    width: rect.width,
  };
}

/** Open a menu at the pointer (viewport coordinates). */
export function anchorAtPointer(
  clientX: number,
  clientY: number,
  options?: { above?: boolean; offset?: number },
): MenuAnchor {
  const offset = options?.offset ?? 4;
  return {
    left: clientX + offset,
    top: clientY,
    placement: options?.above ? 'above-pointer' : 'default',
  };
}
